import type { SoundEventKey, SoundSetting } from "@loot-os/api-contract";
import { isSoundContentType } from "@loot-os/api-contract";
import { toast } from "@loot-os/ui/components/sonner";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { StorefrontSection } from "../components/StorefrontSection";
import { SoundList } from "../components/sounds/SoundList";
import { useStorefrontConfig } from "../lib/storefront";
import { uploadTenantAssetFile } from "../lib/tenantAssets";
import { useTRPC } from "../lib/trpc";
import { useTenantRole } from "../lib/useTenantRole";

const SOUND_KIND = { accept: isSoundContentType, acceptLabel: "only MP3 files are allowed" };

/**
 * The sounds this storefront environment's openers play, per event. Each event inherits the
 * platform default until an editor uploads a replacement, trims it, turns it off, or reverts it.
 * Every change saves at once through `storefront.setSound`.
 */
export function StorefrontSoundsPage() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { can } = useTenantRole();
  const { queryOptions, query } = useStorefrontConfig();

  const set = useMutation(
    trpc.storefront.setSound.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: queryOptions.queryKey });
        toast.success("Sound saved.");
      },
    }),
  );
  const startUpload = useMutation(trpc.storefront.startSoundUpload.mutationOptions());
  const finishUpload = useMutation(trpc.storefront.finishSoundUpload.mutationOptions());

  // Narrowing only: the layout renders a section once the same query has data, so the list never
  // draws "Silent" for a sound that has not loaded.
  if (!query.data) return null;

  const { sounds, environment } = query.data;
  const own = sounds.overrides;

  const onSet = async (event: SoundEventKey, setting: SoundSetting | null) => {
    await set.mutateAsync({ event, setting });
  };
  const onUpload = async (event: SoundEventKey, file: File) => {
    const { publicUrl } = await uploadTenantAssetFile(
      file,
      (input) => startUpload.mutateAsync(input),
      (uploadKey) => finishUpload.mutateAsync({ uploadKey }),
      SOUND_KIND,
    );
    // A file replaces the sound but keeps its trim.
    const current = own[event];
    const gainDb = current && "url" in current ? current.gainDb : undefined;
    await onSet(event, gainDb === undefined ? { url: publicUrl } : { url: publicUrl, gainDb });
  };

  return (
    <StorefrontSection
      title="Sounds"
      description="The sounds this storefront's openers play. Each event uses the platform's sound until you upload your own; trim it, turn it off, or go back to the platform's."
      environment={environment}
    >
      <SoundList
        scope="tenant"
        sounds={{ own, resolved: sounds.resolved }}
        canEdit={can("editor")}
        onUpload={onUpload}
        onSet={onSet}
      />
    </StorefrontSection>
  );
}
